import type { ReactNode } from 'react';
import { Children, isValidElement, useId } from 'react';
import { useSidebarOpenState } from '@backstage/core-components';
import clsx from 'clsx';

import styles from './sidebar-section.module.css';

/** @public */
export interface SidebarSectionProps {
  /** The items of the section. `null` and `undefined` count as absent. */
  children?: ReactNode;
  /** The section header, also the accessible name of the list. */
  label: string;
}

/**
 * A named list of sidebar items.
 *
 * @remarks
 * Open, it shows the label above its items. Compact, the header keeps its
 * height, so no icon moves when the sidebar collapses, and a short divider
 * replaces the text. The text stays in the DOM, hidden, so the list keeps its
 * accessible name in both states. A section whose children all render
 * nothing shows no header either. On mobile core renders the menu open, so
 * the section shows its label there too.
 *
 * @public
 */
export const SidebarSection = ({ children, label }: SidebarSectionProps) => {
  const { isOpen } = useSidebarOpenState();
  const labelId = useId();
  const items = Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={clsx(styles.header, !isOpen && styles.compact)}>
        <div className={styles.label} id={labelId}>
          {label}
        </div>
        {!isOpen && <hr aria-hidden="true" className={styles.divider} />}
      </div>
      <ul aria-labelledby={labelId} className={styles.items}>
        {items.map((child, index) => (
          <li
            key={
              isValidElement(child) && child.key !== null ? child.key : index
            }
            className={styles.item}
          >
            {child}
          </li>
        ))}
      </ul>
    </div>
  );
};
